import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { Bounty, Submission, User } from "@/lib/types";
import { Link, useRouter } from "@/lib/router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
	ArrowLeft,
	Calendar,
	Code,
	DollarSign,
	GitFork,
	Target,
	Trophy,
	ThumbsUp,
} from "lucide-react";

export function Profile({ params }: { params: Record<string, string> }) {
	const { navigate } = useRouter();
	const [user, setUser] = useState<User | null>(null);
	const [bounties, setBounties] = useState<Bounty[]>([]);
	const [submissions, setSubmissions] = useState<Submission[]>([]);

	useEffect(() => {
		api.getUser(params.id).then(setUser);
		api.getBounties().then((all) => {
			setBounties(all.filter((b) => b.posterId === params.id));

			// Fetch submissions across all bounties for this user
			Promise.all(all.map((b) => api.getSubmissions(b.id))).then((allSubs) => {
				const flat = allSubs.flat().filter((s) => s.creatorId === params.id);
				setSubmissions(flat);
			});
		});
	}, [params.id]);

	if (!user) {
		return (
			<div className="container mx-auto px-4 py-16 text-center text-muted-foreground">
				Loading...
			</div>
		);
	}

	return (
		<div className="container mx-auto px-4 py-8 max-w-3xl">
			<button
				onClick={() => navigate("/")}
				className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
			>
				<ArrowLeft className="h-4 w-4" />
				Back
			</button>

			{/* Profile header */}
			<Card className="mb-6">
				<CardContent className="pt-6 pb-6">
					<div className="flex items-start gap-4">
						<div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold shrink-0">
							{user.avatar}
						</div>
						<div className="flex-1">
							<h1 className="text-xl font-bold">{user.username}</h1>
							<p className="text-sm text-muted-foreground mt-1">{user.bio}</p>
							<div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
								<Calendar className="h-3 w-3" />
								Joined {new Date(user.joinedAt).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
							</div>
						</div>
					</div>

					<Separator className="my-4" />

					<div className="grid grid-cols-4 gap-4 text-center">
						<div>
							<div className="flex items-center justify-center gap-1 text-lg font-bold">
								<Trophy className="h-4 w-4 text-amber-500" />
								{user.wins}
							</div>
							<p className="text-xs text-muted-foreground">Wins</p>
						</div>
						<div>
							<div className="flex items-center justify-center gap-1 text-lg font-bold">
								<DollarSign className="h-4 w-4 text-emerald-500" />
								{user.totalEarnings}
							</div>
							<p className="text-xs text-muted-foreground">Earned</p>
						</div>
						<div>
							<div className="flex items-center justify-center gap-1 text-lg font-bold">
								<Code className="h-4 w-4 text-blue-500" />
								{user.submissionsCreated}
							</div>
							<p className="text-xs text-muted-foreground">Submissions</p>
						</div>
						<div>
							<div className="flex items-center justify-center gap-1 text-lg font-bold">
								<Target className="h-4 w-4 text-purple-500" />
								{user.bountiesPosted}
							</div>
							<p className="text-xs text-muted-foreground">Bounties</p>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Tabs */}
			<Tabs defaultValue="submissions">
				<TabsList className="mb-4">
					<TabsTrigger value="submissions">Submissions ({submissions.length})</TabsTrigger>
					<TabsTrigger value="bounties">Bounties Posted ({bounties.length})</TabsTrigger>
				</TabsList>

				<TabsContent value="submissions">
					{submissions.length === 0 ? (
						<Card>
							<CardContent className="py-12 text-center text-muted-foreground">
								No submissions yet.
							</CardContent>
						</Card>
					) : (
						<div className="space-y-3">
							{submissions.map((sub) => (
								<Link key={sub.id} to={`/bounty/${sub.bountyId}`} className="block group">
									<Card className="transition-colors group-hover:border-primary/30">
										<CardContent className="pt-4 pb-4">
											<div className="flex items-start justify-between gap-3">
												<div className="flex-1 min-w-0">
													<div className="flex items-center gap-2 mb-1">
														{sub.isWinner && (
															<Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-xs gap-1">
																<Trophy className="h-3 w-3" />
																Winner
															</Badge>
														)}
														{sub.isFork && (
															<Badge variant="outline" className="text-xs gap-1">
																<GitFork className="h-3 w-3" />
																Fork
															</Badge>
														)}
													</div>
													<h4 className="font-semibold text-sm group-hover:text-primary transition-colors">
														{sub.title}
													</h4>
													<p className="text-xs text-muted-foreground mt-1 line-clamp-1">
														{sub.description}
													</p>
												</div>
												<div className="flex items-center gap-1 text-sm font-medium shrink-0">
													<ThumbsUp className="h-3.5 w-3.5 text-muted-foreground" />
													{sub.votes}
												</div>
											</div>
										</CardContent>
									</Card>
								</Link>
							))}
						</div>
					)}
				</TabsContent>

				<TabsContent value="bounties">
					{bounties.length === 0 ? (
						<Card>
							<CardContent className="py-12 text-center text-muted-foreground">
								No bounties posted yet.
							</CardContent>
						</Card>
					) : (
						<div className="space-y-3">
							{bounties.map((bounty) => (
								<Link key={bounty.id} to={`/bounty/${bounty.id}`} className="block group">
									<Card className="transition-colors group-hover:border-primary/30">
										<CardContent className="pt-4 pb-4">
											<div className="flex items-start justify-between gap-3">
												<div className="flex-1 min-w-0">
													<h4 className="font-semibold text-sm group-hover:text-primary transition-colors">
														{bounty.title}
													</h4>
													<div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
														<span>{bounty.submissionCount} submissions</span>
														<span>
															{bounty.status === "awarded" ? "Awarded" : bounty.status === "open" ? "Open" : "In Review"}
														</span>
													</div>
												</div>
												<div className="text-right shrink-0">
													<span className="text-lg font-bold text-emerald-600">
														${bounty.bountyAmount}
													</span>
												</div>
											</div>
										</CardContent>
									</Card>
								</Link>
							))}
						</div>
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
}
